import { Box, Typography, Button, Card, useTheme, TextField, Alert, CircularProgress, IconButton, Tooltip, Snackbar } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PixIcon from '@mui/icons-material/Pix';
import InputMask from 'react-input-mask';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { visitorService } from '../../../shared/services/visitors/visitorService';
import { useQuery } from '@tanstack/react-query';
import { eventService } from '../../../shared/services/tenant/eventService';
import { trackPaymentInitiated, trackPurchase } from '../../../shared/analytics/gtm';
import type { TrackableEvent } from '../../../shared/analytics/types';

export default function PixChargePage() {
    const navigate = useNavigate();
    const { id, slug } = useParams();
    const idOrSlug = slug || id;
    const theme = useTheme();
    const location = useLocation();
    
    // Determina o caminho base (novo com slug ou antigo com id)
    const basePath = slug ? `/evento/${slug}` : `/public/event/${id}`;

    // Buscar dados do evento para obter o ID real
    const { data: event } = useQuery({
        queryKey: ['event', idOrSlug],
        queryFn: () => eventService.getPublic(idOrSlug!).then(res => res.data),
        enabled: !!idOrSlug,
    });

    // Pega CPF do state, localStorage é apenas fallback
    const initialCpf = location.state?.cpf || localStorage.getItem('lastCpf') || '';
    
    const [pixInfo, setPixInfo] = useState<any>(null);
    const [cpf, setCpf] = useState(initialCpf);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const purchaseTrackedRef = useRef(false);

    useEffect(() => {
        if (!event || !pixInfo || pixInfo.status !== 'PAID' || purchaseTrackedRef.current) return;
        purchaseTrackedRef.current = true;
        trackPurchase(event as TrackableEvent, 'pix', pixInfo.value, pixInfo.paymentId);
    }, [event, pixInfo]);

    const buscarPixInfo = async () => {
        if (loading || !event?.id) return;
        if (!cpf || cpf.replace(/\D/g, '').length < 11) {
            setError('CPF inválido');
            return;
        }
        
        setLoading(true);
        setError(null);
        setPixInfo(null);
        try {
            const info = await visitorService.pixInfo(cpf, event.id);
            console.log('Pix Info:', info);
            setPixInfo(info);
            trackPaymentInitiated(event as TrackableEvent, 'pix', info?.value ? Number(info.value) : undefined);
        } catch (err: any) {
            setError('Cobrança não encontrada para este CPF.');
        }
        setLoading(false);
    };

    // Busca automaticamente apenas se tem CPF válido e evento carregado
    useEffect(() => {
        if (event?.id && initialCpf && initialCpf.replace(/\D/g, '').length === 11) {
            buscarPixInfo();
        }
    }, [event?.id]);

    const handleCopy = async () => {
        if (pixInfo?.payload) {
            await navigator.clipboard.writeText(pixInfo.payload);
            setCopied(true);
        }
    };

    useEffect(() => {
        if (!pixInfo || !cpf || !id) return;
        // Para o polling se já foi pago, expirado ou já existe pagamento
        if (pixInfo.status === 'PAID' || pixInfo.status === 'EXPIRED' || pixInfo.status === 'ALREADY_PAID') return;

        let stopped = false;
        const fetchPix = async () => {
            try {
                const info = await visitorService.pixInfo(cpf, id);
                console.log('Atualizando Pix Info:', info);
                setPixInfo((prev: any | null) => {
                    if (!prev || prev.status !== info.status || prev.payload !== info.payload) {
                        return info;
                    }
                    return prev;
                });
                // Para o polling se pagou, expirou ou já tem pagamento
                if (info.status === 'PAID' || info.status === 'EXPIRED' || info.status === 'ALREADY_PAID') {
                    stopped = true;
                }
            } catch (err) {
                // opcional: setError('Erro ao atualizar status do Pix.');
            }
        };

        const interval = setInterval(() => {
            if (!stopped) fetchPix();
        }, 10000);

        return () => clearInterval(interval);
    }, [pixInfo, cpf, id]);

    return (
        <Box sx={{ p: { xs: 1, md: 4 }, background: theme.palette.background.default, minHeight: '100vh' }}>
            <Card sx={{
                background: theme.palette.background.paper,
                borderRadius: 4,
                p: 4,
                maxWidth: 600,
                margin: '32px auto',
                boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)',
                textAlign: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                    <PixIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                        Pagamento PIX
                    </Typography>
                </Box>
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Informe o CPF utilizado na inscrição para gerar ou recuperar sua cobrança PIX.
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <InputMask
                        mask="999.999.999-99"
                        value={cpf}
                        onChange={(e) => {
                            setCpf(e.target.value);
                            if (error) setError(null);
                        }}
                    >
                        {(inputProps: any) => (
                            <TextField
                                {...inputProps}
                                label="CPF"
                                placeholder="000.000.000-00"
                                sx={{ width: 250, mb: 2 }}
                                size="small"
                            />
                        )}
                    </InputMask>
                    
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            onClick={buscarPixInfo}
                            disabled={loading || !cpf}
                            startIcon={loading && <CircularProgress size={20} />}
                        >
                            {loading ? 'Buscando...' : 'Buscar cobrança'}
                        </Button>
                    </Box>
                </Box>

                {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
                
               
                {pixInfo && pixInfo.status === 'NOT_PIX' && (
                    <Box sx={{
                        mt: 4,
                        mb: 2,
                        p: 4,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.warning.main}`,
                        background: theme.palette.warning.light + '20',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        <PixIcon 
                            sx={{ 
                                fontSize: 56, 
                                color: 'warning.main',
                                mb: 2
                            }} 
                        />
                        <Typography variant="h6" fontWeight={600} color="warning.main" sx={{ mb: 1 }}>
                            Boleto já existe
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                            {pixInfo.message || 'Já existe um boleto ativo para este participante. Não é possível gerar PIX.'}
                        </Typography>
                        {pixInfo.bankSlipUrl && (
                            <Button
                                variant="contained"
                                color="warning"
                                sx={{ mb: 2 }}
                                onClick={() => window.open(pixInfo.bankSlipUrl, '_blank')}
                            >
                                Visualizar Boleto
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onClick={() => navigate(basePath)}
                        >
                            Voltar para inscrição
                        </Button>
                    </Box>
                )}

                {/* Status: Pagamento já realizado */}
                {pixInfo && pixInfo.status === 'ALREADY_PAID' && (
                    <Box sx={{
                        mt: 4,
                        mb: 2,
                        p: 4,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        background: theme.palette.background.default,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        <CheckCircleIcon 
                            sx={{ 
                                fontSize: 56, 
                                color: 'text.secondary',
                                mb: 2
                            }} 
                        />
                        <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                            Pagamento já realizado
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                            {pixInfo.message || 'O pagamento para este participante já foi processado.'}
                        </Typography>
                        {pixInfo.paymentStatus && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Status: <strong>{pixInfo.paymentStatus === 'confirmed' ? 'Confirmado' : pixInfo.paymentStatus}</strong>
                            </Typography>
                        )}
                        <Button
                            variant="outlined"
                            onClick={() => navigate(basePath)}
                        >
                            Voltar para inscrição
                        </Button>
                    </Box>
                )}

                {/* Status: Pago */}
                {pixInfo && pixInfo.status === 'PAID' && (
                    <Box sx={{
                        mt: 4,
                        mb: 2,
                        p: 4,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.success.main}`,
                        background: theme.palette.success.light + '20',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        <CheckCircleIcon 
                            sx={{ 
                                fontSize: 56, 
                                color: 'success.main',
                                mb: 2
                            }} 
                        />
                        <Typography variant="h6" fontWeight={600} color="success.main" sx={{ mb: 1 }}>
                            Pagamento confirmado!
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                            {pixInfo.message || 'Sua cobrança foi paga com sucesso.'}
                        </Typography>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => navigate(basePath)}
                        >
                            Voltar para inscrição
                        </Button>
                    </Box>
                )}

                {/* Status: Pendente - Mostra QR Code */}
                {pixInfo && pixInfo.success && pixInfo.status !== 'PAID' && pixInfo.status !== 'ALREADY_PAID' && pixInfo.status !== 'EXPIRED' && (
                    <Box sx={{ 
                        mt: 4, 
                        p: 3, 
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        background: theme.palette.background.default
                    }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                            Escaneie o QR Code
                        </Typography>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            mb: 3,
                            p: 2,
                            background: 'white',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`
                        }}>
                            <img
                                src={`data:image/png;base64,${pixInfo.encodedImage}`}
                                alt="QR Code Pix"
                                style={{ maxWidth: 200, width: '100%' }}
                            />
                        </Box>

                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                            Código PIX (Copia e Cola):
                        </Typography>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            p: 2, 
                            background: theme.palette.grey[100], 
                            borderRadius: 2,
                            mb: 2
                        }}>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    wordBreak: 'break-all', 
                                    flex: 1,
                                    fontFamily: 'monospace',
                                    fontSize: 12
                                }}
                            >
                                {pixInfo.payload}
                            </Typography>
                            <Tooltip title="Copiar código PIX">
                                <IconButton onClick={handleCopy} size="small" color="primary">
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary">
                            Válido até: {pixInfo.expirationDate}
                        </Typography>
                    </Box>
                )}

                <Button
                    variant="text"
                    color="primary"
                    sx={{ mt: 4 }}
                    onClick={() => navigate(basePath)}
                >
                    Voltar para inscrição
                </Button>
                
                <Snackbar
                    open={copied}
                    autoHideDuration={2000}
                    onClose={() => setCopied(false)}
                    message="Código PIX copiado!"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                />
            </Card>
        </Box>
    );
}